import { useSyncExternalStore } from 'react';

export const MENU_ATTENTION_STORAGE_KEY = 'Layout.menuAttention';
const CHANGE_EVENT = 'menu-attention-changed';

/**
 * Master switch for the menu's attention sheen — the soft brand-tinted light that crosses a row
 * every few seconds to introduce an entry the user has not discovered yet. Individual entries opt in
 * with `attention: true` (see MenuItemModel); this setting silences all of them at once, which is
 * what the switch on the profile page does. `prefers-reduced-motion` hides the sheen regardless of
 * this setting (see MenuItem / SubMenuItem).
 *
 * On by default: an entry marked as new is meant to be noticed until the user says otherwise.
 *
 * Kept in its own store rather than useLocalStorage because the switch lives on the profile page
 * while the reader is the sidebar, which stays mounted across navigation: useLocalStorage does not
 * sync between components, so the menu would not react until a reload.
 */
export const readMenuAttention = (): boolean => {
  try {
    // Unset means on — only an explicit '0' turns it off.
    return localStorage.getItem(MENU_ATTENTION_STORAGE_KEY) !== '0';
  } catch {
    return true;
  }
};

export const writeMenuAttention = (v: boolean) => {
  try {
    localStorage.setItem(MENU_ATTENTION_STORAGE_KEY, v ? '1' : '0');
  } catch {
    // ignore
  }
  // localStorage writes don't fire 'storage' in the same tab — broadcast manually.
  window.dispatchEvent(new CustomEvent(CHANGE_EVENT));
};

export const subscribeMenuAttention = (cb: () => void): (() => void) => {
  const onStorage = (e: StorageEvent) => {
    if (e.key === MENU_ATTENTION_STORAGE_KEY) cb();
  };
  window.addEventListener('storage', onStorage);
  window.addEventListener(CHANGE_EVENT, cb);
  return () => {
    window.removeEventListener('storage', onStorage);
    window.removeEventListener(CHANGE_EVENT, cb);
  };
};

export const useMenuAttention = (): boolean => useSyncExternalStore(subscribeMenuAttention, readMenuAttention, () => true);
